import { defaultsBlockers } from '@/pages/game/live-events/model/eventModel.js';
import { expandOccurrences, scheduleFrom, scheduleStartInstant } from '@/pages/game/live-events/model/schedule.js';
import { changesSomething } from '@/pages/game/live-events/model/testRun.js';
import { EVENT_SYSTEM } from '@/pages/game/live-events/model/vocab.js';
import { proposalView } from '../model/taskChat.js';
import { manualResult, processTasks, ruleBreaksFor } from './mockProcessing.js';
import { DAY, HOUR, iso, seedWorld } from './mockSeed.js';

const STORE_PREFIX = 'geoplay.upcoming.mock.v2';
const LATENCY_MS = 250;
const RUN_MS = 3500;
const STUCK_AFTER_MS = 10 * 60000;
const STUCK_REASON = 'Processing took too long. Run the tasks again.';
const PASS_EVERY_MS = 15 * 60000;
const NO_TASK_LIST = 'No task list — add one before planning this event.';
const NO_CONFIG = 'No configs attached — link one to stage changes.';
const NO_LENGTH = "No length — set how long each run lasts on the event, or this date can't go out.";
const NO_END = 'This date has no end. Give the event (or the placement) a duration, or plan a window.';
const PERSON_STEPS = new Set(['NOT_STARTED', 'OPEN', 'NEEDS_PERSON', 'READY_TO_STAGE', 'STAGED']);
const AUTO_PROCESSING_ON = true;
const DEFAULT_MAX_LEAD_DAYS = 30;
const DEFAULT_MAX_DATES_PER_RUN = 10;
const MAX_DATES_PER_RUN = 50;
const PROCESS_NOW_OFF = 'Auto-processing is switched off here, so Process now is not available. Plan the date step by step.';
const RUNNER_OFF = 'Auto-processing is switched off here, so the runner does not run.';
const GAME_OFF = 'This game does not process dates automatically; turn processDates on first.';
const NOTHING_TO_STAGE = "Nothing to stage: neither the event's copy nor its tasks change a config value";
const CHANGED_SINCE_CHAT = 'The plan changed since the chat worked this out. Ask the chat again.';
const ADJUST = 'Adjust';
const UNTOUCHED = new Set(['NOT_STARTED', 'OPEN']);
const LEAD_BANDS = [[48 * HOUR, 2 * HOUR], [14 * DAY, 48 * HOUR], [62 * DAY, 5 * DAY]];
const RARE_LEAD = 7 * DAY;
const CYCLE_UNIT = { MINUTE: 60000, HOUR, DAY, WEEK: 7 * DAY, MONTH: 30 * DAY, YEAR: 365 * DAY };
const memory = new Map();

const failure = (status, message) => {
    const error = new Error(message);
    error.response = { status, data: { message, detailedMessage: message, errorCode: status === 404 ? 'ENTITY_NOT_FOUND' : 'GS-1001' } };
    return error;
};

const refused = (message) => failure(400, message);
const notFound = (what) => failure(404, `${what} not found`);

const nextId = (db, prefix) => {
    db.seq += 1;
    return `${prefix}-${db.seq}`;
};

const byStart = (a, b) => Date.parse(a.occurrenceStart) - Date.parse(b.occurrenceStart) || a.timelineItemId.localeCompare(b.timelineItemId);

const scheduleOf = (event, placement) => ({ ...scheduleFrom(event), ...(placement.scheduleOverride || {}) });

const occurrences = (event, placement, fromMs, toMs) => {
    const schedule = scheduleOf(event, placement);
    const anchor = scheduleStartInstant(schedule, 'UTC');
    if (anchor == null) return [];
    const floor = Math.max(fromMs, Date.parse(placement.startAt));
    const length = (schedule.durationSeconds || 0) * 1000;
    return expandOccurrences(schedule, anchor, 'UTC', Math.ceil((toMs - anchor) / DAY) + 1)
        .filter((ms) => ms >= floor && ms <= toMs)
        .map((ms) => ({ startMs: ms, endMs: length ? ms + length : null, durationSeconds: schedule.durationSeconds || null }));
};

const eventOf = (db, id) => {
    const event = db.events.find((e) => e.id === id);
    if (!event) throw notFound('Live event');
    return event;
};

const instanceOf = (db, id) => {
    const inst = db.instances.find((i) => i.id === id);
    if (!inst) throw notFound('Live event instance');
    return inst;
};

const planOf = (db, instanceId) => db.plans.find((p) => p.instanceId === instanceId) || null;

const forEvent = (snippet, eventId) => snippet.system === EVENT_SYSTEM && (!snippet.systemId || snippet.systemId === eventId);

const instanceAt = (db, timelineItemId, startMs) => db.instances
    .find((i) => i.timelineItemId === timelineItemId && Date.parse(i.occStart) === startMs) || null;

const taskListsOf = (db, eventId) => db.taskLists.filter((l) => l.beatId === eventId)
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.sortOrder - b.sortOrder);

const windowOf = (db, inst) => {
    const placement = db.placements.find((p) => p.id === inst.timelineItemId);
    const event = eventOf(db, inst.beatId);
    const startMs = Date.parse(inst.occStart);
    const occ = placement ? occurrences(event, placement, startMs, startMs)[0] : null;
    return { startMs, endMs: occ?.endMs ?? (event.durationSeconds ? startMs + event.durationSeconds * 1000 : null) };
};

const activeTasks = (plan) => plan.tasks.filter((t) => !t.dropped).sort((a, b) => a.sortOrder - b.sortOrder);
const isResolved = (task) => task.actor !== 'MANUAL' || task.done || !!task.skipReason;
const hasEnded = (plan, now) => !!plan.windowEnd && now >= Date.parse(plan.windowEnd);

const isStuck = (plan, now) => plan.processingState === 'PROCESSING'
    && (!plan.processingStartedAt || now - Date.parse(plan.processingStartedAt) > STUCK_AFTER_MS);

const effectiveProcessingState = (plan, now) => (isStuck(plan, now) ? 'FAILED' : plan.processingState || 'IDLE');

const stagingBlockers = (plan, now) => {
    const out = [];
    if (plan.publishedAt) out.push('The date is already published');
    if (hasEnded(plan, now)) out.push('The date has ended');
    const state = effectiveProcessingState(plan, now);
    if (state === 'PROCESSING') out.push('The tasks are still running');
    else if (state === 'FAILED') out.push(`The last run failed${plan.failureReason ? `: ${plan.failureReason}` : ''}`);
    else if (plan.processedRevision !== plan.revision) out.push('Run the tasks again after the last change');
    activeTasks(plan).forEach((t) => {
        const status = t.result?.status;
        if (!t.result && t.actor !== 'MANUAL') out.push(`"${t.title}" has not run yet`);
        else if (status === 'NEEDS_INPUT') out.push(`"${t.title}" needs detail`);
        else if (status === 'NOT_ACTIONABLE') out.push(`"${t.title}" is unclear: reword, adjust or drop it`);
        else if (status === 'FAILED') out.push(`"${t.title}" could not be finished: run the tasks again`);
        else if (t.result?.ruleBreaks?.length) out.push(`"${t.title}" breaks a rule: ${t.result.ruleBreaks.join(', ')}`);
    });
    return out;
};

const publishBlockers = (plan, now) => {
    const out = stagingBlockers(plan, now);
    if (plan.stagedRevision !== plan.revision) out.push('See what will change for the latest plan first');
    activeTasks(plan).filter((t) => !isResolved(t)).forEach((t) => out.push(`"${t.title}" is not done or skipped`));
    return out;
};

const dateState = (plan, now) => {
    if (hasEnded(plan, now)) return 'ENDED';
    if (plan.publishedAt) return now >= Date.parse(plan.windowStart) ? 'LIVE' : 'READY';
    const state = effectiveProcessingState(plan, now);
    if (state === 'PROCESSING') return 'PROCESSING';
    const needsPerson = state === 'FAILED' || plan.processedRevision !== plan.revision
        || (!!plan.processNote && plan.stagedRevision !== plan.revision)
        || activeTasks(plan).some((t) => t.result?.blocksStaging);
    if (needsPerson) return 'NEEDS_PERSON';
    return plan.stagedRevision === plan.revision ? 'READY' : 'PLANNED';
};

const ensureEditable = (plan, now) => {
    if (plan.publishedAt) throw refused('The date is published; its plan can no longer change');
    if (hasEnded(plan, now)) throw refused('The date has ended; its plan can no longer change');
};

const needsRun = (task) => !task.result || task.result.blocksStaging || task.result.status !== 'COMPLETED';

// Mirrors LiveEventRunPlan.runSelection: the changed tasks and any that never ran; null runs every AI task.
const runSelection = (plan, changed) => activeTasks(plan)
    .filter((t) => t.actor !== 'MANUAL' && (changed == null || changed.includes(t.id) || !t.result))
    .map((t) => t.id);

const pendingTasks = (plan) => activeTasks(plan).filter((t) => t.actor !== 'MANUAL' && needsRun(t)).map((t) => t.id);

const stillProcessed = (plan, now) => effectiveProcessingState(plan, now) === 'IDLE' && plan.processedRevision === plan.revision;

const mergeAnswers = (existing, replies) => [
    ...existing.filter((old) => !replies.some((r) => r.question !== ADJUST && r.question === old.question)),
    ...replies,
];

const startRun = (plan, now, run) => {
    ensureEditable(plan, now);
    const pending = plan.processingState === 'PROCESSING' ? plan.runTaskIds || [] : [];
    plan.runTaskIds = [...new Set([...pending, ...run])];
    plan.processingState = 'PROCESSING';
    plan.processingStartedAt = iso(now);
    plan.processingRevision = plan.revision;
    plan.failureReason = null;
    plan.processNote = null;
    plan.updatedAt = iso(now);
};

const inventoryFor = (db, event, plan) => ({
    configs: event.configLinks
        .map((l) => db.configFiles.find((f) => f.id === l.configFileId && !f.archived))
        .filter(Boolean)
        .map((f) => ({ fileId: f.id, name: f.name, content: f.content })),
    segments: db.segments.map((s) => ({ id: s.versionInfo?.parentEntityId || s.id, name: s.segmentName })),
    audience: event.segmentIds,
    knowledge: db.snippets.filter((s) => forEvent(s, event.id)).map((s) => ({ id: s.id, title: s.title, body: s.body })),
    rules: db.snippets.filter((s) => forEvent(s, event.id) && s.rule).map((s) => s.rule),
    lastRuns: db.runs.filter((r) => r.liveEventId === event.id)
        .sort((a, b) => b.ranAt.localeCompare(a.ranAt))
        .slice(0, 3)
        .map((r) => ({ ranAt: r.ranAt, title: r.title, done: r.notes, result: r.results })),
    occurrence: { start: plan.windowStart, end: plan.windowEnd },
});

const applyResults = (db, plan, now) => {
    const event = eventOf(db, plan.liveEventId);
    const results = processTasks(activeTasks(plan), inventoryFor(db, event, plan), iso(now), new Set(plan.runTaskIds || []));
    plan.tasks = plan.tasks.map((t) => (results[t.id] ? { ...t, result: results[t.id] } : t));
    plan.processingState = 'IDLE';
    plan.processedRevision = plan.processingRevision;
    plan.runTaskIds = null;
    plan.failureReason = null;
    plan.updatedAt = iso(now);
};

const windowEndFor = (plan, actions) => {
    const win = [...actions].reverse().find((a) => a.type === 'WINDOW' && a.hours);
    return win ? iso(Date.parse(plan.windowStart) + win.hours * HOUR) : plan.windowEnd;
};

// How many hours the plan's done tasks set the date to last, or null: the last WINDOW change wins.
const plannedWindowHours = (plan) => {
    const actions = activeTasks(plan).filter((t) => t.result && changesSomething(t.result)).flatMap((t) => t.result.actions);
    return [...actions].reverse().find((a) => a.type === 'WINDOW' && a.hours)?.hours ?? null;
};

const overlaps = (aStart, aEnd, bStart, bEnd) => Date.parse(aStart) < (bEnd ? Date.parse(bEnd) : Infinity)
    && Date.parse(bStart) < (aEnd ? Date.parse(aEnd) : Infinity);

const stagePlan = (db, plan, now) => {
    const blockers = stagingBlockers(plan, now);
    if (blockers.length) throw refused(blockers.join('; '));
    const event = eventOf(db, plan.liveEventId);
    const changing = activeTasks(plan).filter((t) => t.result && changesSomething(t.result));
    const actions = changing.flatMap((t) => t.result.actions);
    const changes = changing.flatMap((t) => t.result.changes);
    const windowEnd = windowEndFor(plan, actions);
    // As on the server, a game-server event stages as it did before the length rule, end or not.
    if (event?.owner !== 'GAME_SERVER' && (!windowEnd || Date.parse(windowEnd) <= Date.parse(plan.windowStart))) {
        throw refused(NO_END);
    }
    const audience = [...new Set(actions.filter((a) => a.type === 'AUDIENCE').map((a) => a.segmentId))];
    db.plans.filter((other) => other.id !== plan.id).forEach((other) => other.stagedCopies.forEach((copy) => {
        if (!overlaps(plan.windowStart, windowEnd, copy.windowStart, copy.windowEnd)) return;
        const clash = changes.find((c) => c.fileId === copy.fileId && copy.changes.some((x) => x.key === c.key));
        if (clash) {
            throw refused(`"${clash.key}" is also written by "${eventOf(db, other.liveEventId).name}" in an overlapping window. Two events cannot change the same value at the same time.`);
        }
    }));
    const byFile = new Map();
    changes.forEach((c) => {
        const list = byFile.get(c.fileId) || [];
        const seen = list.find((x) => x.key === c.key);
        if (seen) seen.after = c.after;
        else list.push({ key: c.key, before: c.before, after: c.after });
        byFile.set(c.fileId, list);
    });
    plan.stagedCopies = [...byFile.entries()].map(([fileId, list]) => ({
        fileId,
        baseBranchId: `gameConfigBranch-${fileId}-base`,
        branchId: plan.stagedCopies.find((c) => c.fileId === fileId)?.branchId || nextId(db, 'gameConfigBranch'),
        state: 'DRAFT',
        changes: list,
        windowStart: plan.windowStart,
        windowEnd,
        segmentIds: audience.length ? audience : event.segmentIds,
    }));
    plan.stagedRevision = plan.revision;
    plan.stagedAt = iso(now);
    plan.updatedAt = iso(now);
};

const settle = (db, plan, now) => {
    if (plan.processingState !== 'PROCESSING' || now - Date.parse(plan.processingStartedAt) < RUN_MS) return;
    applyResults(db, plan, now);
    if (!plan.processRequested) return;
    plan.processRequested = false;
    const blockers = stagingBlockers(plan, now);
    if (blockers.length) {
        plan.processNote = `Stopped: ${blockers.join('; ')}`;
        return;
    }
    try {
        if (!activeTasks(plan).some((t) => t.result && changesSomething(t.result) && t.result.changes.length)) throw refused(NOTHING_TO_STAGE);
        stagePlan(db, plan, now);
        plan.processNote = null;
    } catch (e) {
        plan.processNote = `Stopped: ${e.message}`;
    }
};

const publishPlan = (db, plan, now) => {
    const blockers = publishBlockers(plan, now);
    if (blockers.length) throw refused(blockers.join('; '));
    const event = eventOf(db, plan.liveEventId);
    const rules = db.snippets.filter((s) => forEvent(s, event.id) && s.rule).map((s) => s.rule);
    const broken = plan.stagedCopies.flatMap((copy) => copy.changes.flatMap((c) => ruleBreaksFor(rules, copy.fileId, c.key, c.after)));
    if (broken.length) throw refused(`Rules broken: ${broken.join('; ')}`);
    plan.stagedCopies = plan.stagedCopies.map((c) => ({ ...c, state: 'ACTIVE' }));
    plan.publishedAt = iso(now);
    plan.updatedAt = iso(now);
    const inst = instanceOf(db, plan.instanceId);
    inst.publishedAt = plan.publishedAt;
    inst.updatedAt = plan.publishedAt;
};

const runState = (inst, now) => {
    if (!inst) return { status: 'NOT_STARTED', reason: null };
    if (inst.skippedAt) return { status: 'SKIPPED', reason: inst.skipReason };
    if (inst.cancelledAt) return { status: 'CANCELLED', reason: null };
    if (inst.publishedAt) return { status: now < Date.parse(inst.occStart) ? 'SCHEDULED' : 'LIVE', reason: null };
    if (now >= Date.parse(inst.occStart)) return { status: 'MISSED', reason: 'Not published before it started; normal branches served instead.' };
    return { status: 'OPEN', reason: 'Nothing is planned yet.' };
};

const progressOf = (inst) => ({
    started: !!inst,
    stage: inst?.stage || null,
    nextStage: inst ? 'PLAN' : 'OVERVIEW',
    planDone: false,
    refineDone: false,
    stageDone: false,
    published: !!inst?.publishedAt,
    nextPhase: inst?.publishedAt ? 'DONE' : 'PLAN',
});

const instanceDto = (inst, now) => {
    const state = runState(inst, now);
    return {
        ...inst,
        progress: progressOf(inst),
        state: { status: state.status, reason: state.reason, planned: false, staged: false, published: !!inst.publishedAt, measured: false, openQuestions: 0 },
    };
};

const planDto = (plan, now) => ({
    id: plan.id,
    instanceId: plan.instanceId,
    liveEventId: plan.liveEventId,
    taskListId: plan.taskListId,
    taskListName: plan.taskListName,
    revision: plan.revision,
    dateState: dateState(plan, now),
    processingState: effectiveProcessingState(plan, now),
    processingStartedAt: plan.processingStartedAt,
    failureReason: isStuck(plan, now) ? STUCK_REASON : plan.failureReason,
    windowStart: plan.windowStart,
    windowEnd: plan.windowEnd,
    stagedAt: plan.stagedAt,
    stagedCopies: plan.stagedCopies.map(({ fileId, baseBranchId, branchId }) => ({ fileId, baseBranchId, branchId })),
    publishedAt: plan.publishedAt,
    concludedAt: null,
    processRequested: plan.processRequested,
    processNote: plan.processNote,
    stagingBlockers: stagingBlockers(plan, now),
    publishBlockers: publishBlockers(plan, now),
    tasks: [...plan.tasks].sort((a, b) => a.sortOrder - b.sortOrder),
});

const dateBlockers = (plan, state, now) => {
    const out = [];
    if (plan.processNote && plan.stagedRevision !== plan.revision) out.push(plan.processNote);
    if (state === 'NEEDS_PERSON') out.push(...stagingBlockers(plan, now));
    if (state === 'READY' && !plan.publishedAt) out.push(...publishBlockers(plan, now));
    return [...new Set(out)];
};

const withRunPlan = (state, plan, ds, blockers) => {
    if (!plan || !PERSON_STEPS.has(state.status)) return state;
    const first = blockers[0] || null;
    if (ds === 'PROCESSING') return { status: 'OPEN', reason: "The plan's tasks are running." };
    if (ds === 'NEEDS_PERSON') return { status: 'NEEDS_PERSON', reason: first || 'The plan needs a person.' };
    if (ds === 'PLANNED') return { status: 'READY_TO_STAGE', reason: first };
    if (ds === 'READY') return { status: 'STAGED', reason: first };
    return state;
};

const stoppedError = (inst, state) => (state.status === 'OPEN' && inst?.autoError ? inst.autoError : null);

// The server's order: automation stopped, no config, no length, DEFAULTS can't ship, no task list, then the date's own.
const statusReason = (event, inst, state, facts) => {
    if (PERSON_STEPS.has(state.status)) {
        const stopped = stoppedError(inst, state);
        if (stopped) return `Automation stopped: ${stopped}`;
        if (!facts.hasConfig) return NO_CONFIG;
        if (!facts.hasEnd && event.owner !== 'GAME_SERVER') return NO_LENGTH;
        if (event.runMode === 'DEFAULTS') {
            const blockers = defaultsBlockers(event);
            if (blockers.length) return `DEFAULTS can't ship: ${blockers[0]}`;
        } else if (facts.unplanned && !facts.hasTaskList) {
            return NO_TASK_LIST;
        }
    }
    return state.reason;
};

const rowOf = (db, placement, flywheel, event, occ, now) => {
    const inst = instanceAt(db, placement.id, occ.startMs);
    const plan = inst ? planOf(db, inst.id) : null;
    if (plan) settle(db, plan, now);
    const ds = plan ? dateState(plan, now) : 'NOT_PLANNED';
    const blockers = plan ? dateBlockers(plan, ds, now) : [];
    const state = withRunPlan(runState(inst, now), plan, ds, blockers);
    const lists = taskListsOf(db, event.id);
    const progress = progressOf(inst);
    const facts = {
        hasConfig: event.configLinks.length > 0,
        hasEnd: occ.durationSeconds > 0 || (plan ? plannedWindowHours(plan) > 0 : false),
        hasTaskList: lists.length > 0,
        unplanned: ds === 'NOT_PLANNED' && progress.nextPhase === 'PLAN',
    };
    return {
        flywheelId: flywheel.id,
        flywheelName: flywheel.name,
        flywheelColor: flywheel.color,
        flywheelIcon: flywheel.icon,
        laneId: placement.laneId,
        timelineItemId: placement.id,
        beatId: event.id,
        title: placement.title || event.name,
        category: event.category,
        occurrenceStart: iso(occ.startMs),
        occurrenceEnd: occ.endMs ? iso(occ.endMs) : null,
        durationSeconds: occ.durationSeconds,
        intervalValue: event.intervalValue,
        intervalUnit: event.intervalUnit,
        overridden: !!placement.scheduleOverride,
        instanceId: inst?.id || null,
        progress,
        status: state.status,
        statusReason: statusReason(event, inst, state, facts),
        autoError: stoppedError(inst, state),
        needsPerson: PERSON_STEPS.has(state.status),
        readinessWarning: progress.nextPhase === 'PLAN' && !lists.length && event.runMode !== 'DEFAULTS' ? NO_TASK_LIST : null,
        dateState: ds,
        dateBlockers: blockers,
        taskLists: lists.map((l) => ({ id: l.id, name: l.name, isDefault: l.isDefault })),
        workspacePath: `/v1/web/live-event-instances/placements/${placement.id}/occurrences/${occ.startMs}`,
    };
};

const upcomingRows = (db, horizonDays, now, filters = {}) => {
    const toMs = now + horizonDays * DAY;
    const rows = [];
    db.placements.forEach((placement) => {
        if (filters.flywheelId && placement.flywheelId !== filters.flywheelId) return;
        const event = db.events.find((e) => e.id === placement.beatId);
        const flywheel = db.flywheels.find((f) => f.id === placement.flywheelId);
        if (!event || !flywheel || placement.kind !== 'BEAT') return;
        if (filters.category && event.category !== filters.category) return;
        occurrences(event, placement, now, toMs).forEach((occ) => rows.push(rowOf(db, placement, flywheel, event, occ, now)));
    });
    const statuses = String(filters.status || '').split(',').map((s) => s.trim()).filter(Boolean);
    return rows
        .filter((r) => !statuses.length || statuses.includes(r.status))
        .filter((r) => String(filters.needsPerson) !== 'true' || r.needsPerson)
        .sort(byStart);
};

const openInstance = (db, timelineItemId, occStartMs, now) => {
    const placement = db.placements.find((p) => p.id === timelineItemId);
    if (!placement) throw notFound('Placement');
    const event = eventOf(db, placement.beatId);
    if (!occurrences(event, placement, occStartMs, occStartMs).some((o) => o.startMs === occStartMs)) {
        throw refused('No date of this placement starts at that time.');
    }
    const existing = instanceAt(db, timelineItemId, occStartMs);
    if (existing) return existing;
    const inst = {
        id: nextId(db, 'liveEventInstance'),
        timelineItemId,
        occStart: iso(occStartMs),
        beatId: event.id,
        stage: 'OVERVIEW',
        segmentIds: [],
        publishedAt: null,
        autoError: null,
        autoRunAt: null,
        plan: null,
        window: null,
        staged: null,
        publishedById: null,
        endedEarlyAt: null,
        cancelledAt: null,
        skippedAt: null,
        skipReason: null,
        outcome: null,
        createdAt: iso(now),
        updatedAt: iso(now),
    };
    db.instances.push(inst);
    return inst;
};

const newPlan = (db, inst, list, now) => {
    const window = windowOf(db, inst);
    const plan = {
        id: nextId(db, 'liveEventRunPlan'),
        instanceId: inst.id,
        liveEventId: inst.beatId,
        taskListId: list?.id || null,
        taskListName: list?.name || null,
        revision: 1,
        processingState: 'IDLE',
        processingStartedAt: null,
        processingRevision: null,
        processedRevision: null,
        runTaskIds: null,
        failureReason: null,
        tasks: [...(list?.items || [])].sort((a, b) => a.sortOrder - b.sortOrder).map((item, i) => ({
            id: nextId(db, 'liveEventRunTask'),
            sourceItemId: item.id,
            title: item.title,
            description: item.description,
            actor: item.actor || 'AI',
            sortOrder: i,
            answers: [],
            dropped: false,
            done: false,
            skipReason: null,
            result: null,
            completion: null,
        })),
        windowStart: iso(window.startMs),
        windowEnd: window.endMs ? iso(window.endMs) : null,
        stagedAt: null,
        stagedRevision: null,
        stagedCopies: [],
        publishedAt: null,
        processRequested: false,
        processNote: null,
        createdAt: iso(now),
        updatedAt: iso(now),
    };
    startRun(plan, now, runSelection(plan, null));
    db.plans.push(plan);
    return plan;
};

const createPlan = (db, instanceId, taskListId, now) => {
    if (!taskListId) throw refused('A plan requires a taskListId');
    const inst = instanceOf(db, instanceId);
    if (planOf(db, instanceId)) throw refused('This date already has a plan; reset it to plan from another list');
    const list = db.taskLists.find((l) => l.id === taskListId);
    if (!list) throw notFound('Task list');
    if (list.beatId !== inst.beatId) throw refused('That task list belongs to another live event');
    return newPlan(db, inst, list, now);
};

const processDate = (db, inst, now) => {
    const existing = planOf(db, inst.id);
    if (existing) settle(db, existing, now);
    const plan = existing || newPlan(db, inst, taskListsOf(db, inst.beatId).find((l) => l.isDefault) || null, now);
    if (plan.stagedRevision === plan.revision) return plan;
    ensureEditable(plan, now);
    plan.processRequested = true;
    plan.processNote = null;
    if (effectiveProcessingState(plan, now) !== 'PROCESSING') startRun(plan, now, runSelection(plan, null));
    return plan;
};

const requireText = (text, message) => {
    if (!String(text || '').trim()) throw refused(message);
};

const taskOf = (plan, taskId) => {
    const task = plan.tasks.find((t) => t.id === taskId);
    if (!task) throw refused(`No task ${taskId} on this date's plan`);
    return task;
};

// `run` names the tasks the edit runs (null: none); an edit while a run is going runs every AI task.
const edit = (plan, now, run, fn) => {
    ensureEditable(plan, now);
    const running = effectiveProcessingState(plan, now) === 'PROCESSING';
    const processed = stillProcessed(plan, now);
    fn();
    plan.revision += 1;
    if (processed) plan.processedRevision = plan.revision;
    plan.updatedAt = iso(now);
    const tasks = running ? runSelection(plan, null) : run ? runSelection(plan, run) : [];
    if (tasks.length) startRun(plan, now, tasks);
};

const saveAnswersToList = (db, task, answers) => {
    const item = db.taskLists.flatMap((l) => l.items).find((i) => i.id === task.sourceItemId);
    if (!item) return;
    const lines = answers.map((a) => `${a.question}: ${a.answer}`);
    item.description = [item.description, ...lines].filter(Boolean).join('\n');
};

const runPass = (db, now) => {
    const settings = db.autoSettings;
    const groups = new Map();
    upcomingRows(db, settings.maxLeadDays, now).forEach((row) => {
        const list = groups.get(row.timelineItemId) || [];
        if (list.length < settings.occurrencesPerPlacement) list.push(row);
        groups.set(row.timelineItemId, list);
    });
    let created = 0;
    [...groups.values()].flat().slice(0, settings.maxOccurrencesPerRun).forEach((row) => {
        if (row.instanceId) return;
        openInstance(db, row.timelineItemId, Date.parse(row.occurrenceStart), now);
        created += 1;
    });
    settings.lastRunAt = iso(now);
    settings.lastRunCreated = created;
};

const leadOf = (event, intervalValue, intervalUnit, maxLeadDays) => {
    const cycle = intervalValue > 0 && CYCLE_UNIT[intervalUnit] ? intervalValue * CYCLE_UNIT[intervalUnit] : null;
    const band = cycle == null ? RARE_LEAD : LEAD_BANDS.find(([upTo]) => cycle <= upTo)?.[1] ?? RARE_LEAD;
    return Math.min(event.reviewLeadHours > 0 ? event.reviewLeadHours * HOUR : band, Math.max(1, maxLeadDays) * DAY);
};

const isDue = (db, row, now, maxLeadDays) => {
    const event = db.events.find((e) => e.id === row.beatId);
    if (!event || event.owner === 'GAME_SERVER' || !event.configLinks?.length) return false;
    if (row.dateState !== 'NOT_PLANNED' || row.autoError || !UNTOUCHED.has(row.status)) return false;
    const start = Date.parse(row.occurrenceStart);
    return start > now && now >= start - leadOf(event, row.intervalValue, row.intervalUnit, maxLeadDays);
};

const runDates = (db, now, flywheelId = null) => {
    const settings = db.autoSettings;
    const maxLeadDays = settings.maxLeadDays > 0 ? settings.maxLeadDays : DEFAULT_MAX_LEAD_DAYS;
    const cap = settings.maxDatesPerRun > 0 ? settings.maxDatesPerRun : DEFAULT_MAX_DATES_PER_RUN;
    const longest = Math.max(RARE_LEAD, ...db.events.map((e) => leadOf(e, e.intervalValue, e.intervalUnit, maxLeadDays)));
    const horizonDays = Math.max(1, Math.min(maxLeadDays, Math.ceil(longest / DAY) + 1));
    const due = upcomingRows(db, horizonDays, now, { flywheelId }).filter((row) => isDue(db, row, now, maxLeadDays)).slice(0, cap);
    let started = 0;
    due.forEach((row) => {
        const inst = row.instanceId ? instanceOf(db, row.instanceId) : openInstance(db, row.timelineItemId, Date.parse(row.occurrenceStart), now);
        try {
            processDate(db, inst, now);
            inst.autoError = null;
            started += 1;
        } catch (e) {
            inst.autoError = String(e.message).slice(0, 300);
        }
        inst.autoRunAt = iso(now);
        inst.updatedAt = iso(now);
    });
    settings.lastProcessedAt = iso(now);
    settings.lastRunProcessed = started;
    return started;
};

const settingsDto = (settings) => ({
    ...settings,
    processDates: !!settings.processDates,
    maxDatesPerRun: settings.maxDatesPerRun ?? DEFAULT_MAX_DATES_PER_RUN,
    lastProcessedAt: settings.lastProcessedAt ?? null,
    lastRunProcessed: settings.lastRunProcessed ?? null,
    processingAvailable: AUTO_PROCESSING_ON,
});

const planFor = (db, instanceId, now) => {
    instanceOf(db, instanceId);
    const plan = planOf(db, instanceId);
    if (!plan) throw notFound('Run plan');
    settle(db, plan, now);
    return plan;
};

const planResponse = (plan, now) => ({ liveEventRunPlan: planDto(plan, now) });

const withPlan = (fn) => (db, m, req, now) => {
    const plan = planFor(db, m[1], now);
    fn(db, plan, req, now, m);
    return planResponse(plan, now);
};

const ROUTES = [
    ['get', /^\/upcoming-live-events$/, (db, m, { params }, now) => {
        const settings = db.autoSettings;
        if (settings.enabled && (!settings.lastRunAt || now - Date.parse(settings.lastRunAt) >= PASS_EVERY_MS)) runPass(db, now);
        if (AUTO_PROCESSING_ON && settings.processDates
            && (!settings.lastProcessedAt || now - Date.parse(settings.lastProcessedAt) >= PASS_EVERY_MS)) runDates(db, now);
        const horizonDays = Math.min(366, Math.max(1, Number(params?.horizonDays) || 90));
        const rows = upcomingRows(db, horizonDays, now, params || {});
        const days = [];
        rows.forEach((row) => {
            const day = row.occurrenceStart.slice(0, 10);
            const last = days[days.length - 1];
            if (last?.day === day) last.events.push(row);
            else days.push({ day, events: [row] });
        });
        return { upcoming: { timezone: 'UTC', horizonDays, returnedCount: rows.length, truncated: false, nextCursor: null, days } };
    }],
    ['get', /^\/live-event-auto-settings$/, (db) => ({ autoSettings: settingsDto(db.autoSettings) })],
    ['put', /^\/live-event-auto-settings$/, (db, m, { data }) => {
        const s = db.autoSettings;
        if (data?.occurrencesPerPlacement != null && !(data.occurrencesPerPlacement >= 1)) throw refused('occurrencesPerPlacement must be at least 1');
        if (data?.maxLeadDays != null && !(data.maxLeadDays >= 1 && data.maxLeadDays <= 366)) throw refused('maxLeadDays must be between 1 and 366');
        if (data?.maxOccurrencesPerRun != null && !(data.maxOccurrencesPerRun >= 1)) throw refused('maxOccurrencesPerRun must be at least 1');
        if (data?.maxDatesPerRun != null && !(data.maxDatesPerRun >= 1 && data.maxDatesPerRun <= MAX_DATES_PER_RUN)) {
            throw refused(`maxDatesPerRun must be between 1 and ${MAX_DATES_PER_RUN}`);
        }
        ['enabled', 'occurrencesPerPlacement', 'maxLeadDays', 'maxOccurrencesPerRun', 'processDates', 'maxDatesPerRun'].forEach((k) => {
            if (data?.[k] != null) s[k] = data[k];
        });
        return { autoSettings: settingsDto(s) };
    }],
    ['post', /^\/live-event-auto-settings\/run$/, (db, m, { params }, now) => {
        if (!AUTO_PROCESSING_ON) throw refused(RUNNER_OFF);
        if (!db.autoSettings.processDates) throw refused(GAME_OFF);
        return { started: runDates(db, now, params?.flywheelId || null) };
    }],
    ['post', /^\/live-event-instances\/placements\/([^/]+)\/occurrences\/(\d+)$/, (db, m, req, now) => ({
        liveEventInstance: instanceDto(openInstance(db, decodeURIComponent(m[1]), Number(m[2]), now), now),
    })],
    ['get', /^\/live-event-instances\/([^/]+)$/, (db, m, req, now) => ({ liveEventInstance: instanceDto(instanceOf(db, m[1]), now) })],
    ['post', /^\/live-event-instances\/([^/]+)\/skip$/, (db, m, { data }, now) => {
        const inst = instanceOf(db, m[1]);
        const reason = String(data?.reason || '').trim();
        if (!reason) throw refused('Say why this date is being skipped.');
        if (reason.length > 300) throw refused('A skip reason can be at most 300 characters.');
        if (inst.publishedAt) throw refused('This date is already published, so it cannot be skipped.');
        inst.skippedAt = iso(now);
        inst.skipReason = reason;
        inst.updatedAt = iso(now);
        return { liveEventInstance: instanceDto(inst, now) };
    }],
    ['post', /^\/live-event-instances\/([^/]+)\/unskip$/, (db, m, req, now) => {
        const inst = instanceOf(db, m[1]);
        inst.skippedAt = null;
        inst.skipReason = null;
        inst.updatedAt = iso(now);
        return { liveEventInstance: instanceDto(inst, now) };
    }],
    ['get', /^\/live-event-instances\/([^/]+)\/plan$/, (db, m, req, now) => planResponse(planFor(db, m[1], now), now)],
    ['post', /^\/live-event-instances\/([^/]+)\/plan$/, (db, m, { data }, now) => planResponse(createPlan(db, m[1], data?.taskListId, now), now)],
    ['delete', /^\/live-event-instances\/([^/]+)\/plan$/, (db, m, req, now) => {
        const plan = planFor(db, m[1], now);
        if (plan.publishedAt) throw refused('The date is published; its plan can no longer change');
        db.plans = db.plans.filter((p) => p.id !== plan.id);
        return { success: true };
    }],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/process$/, (db, m, req, now) => {
        if (!AUTO_PROCESSING_ON) throw refused(PROCESS_NOW_OFF);
        return planResponse(processDate(db, instanceOf(db, m[1]), now), now);
    }],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/run$/, withPlan((db, plan, { params }, now) => {
        if (String(params?.all) === 'true') {
            startRun(plan, now, runSelection(plan, null));
            return;
        }
        if (params?.taskId) {
            const task = taskOf(plan, params.taskId);
            if (task.actor === 'MANUAL' || task.dropped) throw refused('Only an AI task on the plan runs');
            startRun(plan, now, runSelection(plan, [task.id]));
            return;
        }
        const pending = pendingTasks(plan);
        if (pending.length) startRun(plan, now, pending);
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/tasks$/, withPlan((db, plan, { data }, now) => {
        requireText(data?.title, 'A task requires a title');
        edit(plan, now, [], () => plan.tasks.push({
            id: nextId(db, 'liveEventRunTask'),
            sourceItemId: null,
            title: data.title.trim(),
            description: data.description?.trim() || null,
            actor: data.actor === 'MANUAL' ? 'MANUAL' : 'AI',
            sortOrder: Math.max(-1, ...plan.tasks.map((t) => t.sortOrder)) + 1,
            answers: [],
            dropped: false,
            done: false,
            skipReason: null,
            result: data.actor === 'MANUAL' ? manualResult(iso(now)) : null,
            completion: null,
        }));
    })],
    ['put', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/order$/, withPlan((db, plan, { data }, now) => {
        const ids = data?.taskIds || [];
        const known = new Set(plan.tasks.map((t) => t.id));
        if (ids.length !== plan.tasks.length || new Set(ids).size !== ids.length || ids.some((id) => !known.has(id))) {
            throw refused('A reorder must name every task on the plan exactly once');
        }
        edit(plan, now, null, () => plan.tasks.forEach((t) => { t.sortOrder = ids.indexOf(t.id); }));
    })],
    ['put', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/([^/]+)$/, withPlan((db, plan, { data }, now, m) => {
        const task = taskOf(plan, m[2]);
        if (data?.title != null) requireText(data.title, 'A task requires a title');
        edit(plan, now, [task.id], () => {
            if (data?.title != null) task.title = data.title.trim();
            if (data?.description != null) task.description = data.description.trim() || null;
            const actor = data?.actor ? (data.actor === 'MANUAL' ? 'MANUAL' : 'AI') : task.actor;
            if (actor !== task.actor) task.result = actor === 'MANUAL' ? manualResult(iso(now)) : null;
            task.actor = actor;
        });
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/([^/]+)\/(drop|restore)$/, withPlan((db, plan, req, now, m) => {
        const task = taskOf(plan, m[2]);
        edit(plan, now, m[3] === 'drop' ? null : [task.id], () => { task.dropped = m[3] === 'drop'; });
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/([^/]+)\/answers$/, withPlan((db, plan, { data }, now, m) => {
        const task = taskOf(plan, m[2]);
        const replies = (data?.answers || []).map((a) => ({ question: String(a?.question || '').trim(), answer: String(a?.answer || '').trim() }));
        if (!replies.length || replies.some((a) => !a.question || !a.answer)) throw refused('Each answer requires a question and an answer');
        edit(plan, now, [task.id], () => {
            task.answers = [...task.answers.filter((old) => !replies.some((r) => r.question === old.question)), ...replies];
        });
        if (data?.saveToEvent && task.sourceItemId) saveAnswersToList(db, task, replies);
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/([^/]+)\/adjust$/, withPlan((db, plan, { data }, now, m) => {
        const task = taskOf(plan, m[2]);
        requireText(data?.instruction, 'An adjustment requires an instruction');
        edit(plan, now, [task.id], () => { task.answers = [...task.answers, { question: 'Adjust', answer: data.instruction.trim() }]; });
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/([^/]+)\/resolve$/, withPlan((db, plan, { data }, now, m) => {
        ensureEditable(plan, now);
        const task = taskOf(plan, m[2]);
        if (task.actor !== 'MANUAL') throw refused('Only a Manual task is ticked or skipped');
        const skip = String(data?.skipReason || '').trim();
        if (data?.done == null && !skip) throw refused('Resolve a Manual task with done or a skip reason');
        task.done = data?.done === true;
        task.skipReason = task.done || !skip ? null : skip;
        const note = String(data?.note || '').trim();
        task.completion = note || data?.refs?.length ? { note: note || null, refs: data.refs || [] } : null;
        plan.updatedAt = iso(now);
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/tasks\/([^/]+)\/chat-result$/, withPlan((db, plan, { data }, now, m) => {
        ensureEditable(plan, now);
        if (data?.revision == null || !data?.result) throw refused('A chat result requires a revision and a result');
        if (data.revision !== plan.revision || effectiveProcessingState(plan, now) === 'PROCESSING') throw refused(CHANGED_SINCE_CHAT);
        const task = taskOf(plan, m[2]);
        if (task.actor === 'MANUAL' || task.dropped) throw refused('Only an AI task on the plan takes a result from the chat');
        const replies = (data.answers || []).map((a) => ({ question: String(a?.question || '').trim(), answer: String(a?.answer || '').trim() }))
            .filter((a) => a.question && a.answer);
        const processed = stillProcessed(plan, now);
        task.answers = mergeAnswers(task.answers, replies);
        task.result = { ...data.result.mockResult, processedAt: iso(now) };
        plan.revision += 1;
        if (processed) plan.processedRevision = plan.revision;
        plan.updatedAt = iso(now);
        const kept = replies.filter((a) => a.question !== ADJUST);
        if (data.saveToEvent && task.sourceItemId && kept.length) saveAnswersToList(db, task, kept);
    })],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/stage$/, withPlan((db, plan, req, now) => stagePlan(db, plan, now))],
    ['post', /^\/live-event-instances\/([^/]+)\/plan\/publish$/, withPlan((db, plan, req, now) => publishPlan(db, plan, now))],
    ['get', /^\/live-event-instances\/([^/]+)\/configs$/, (db, m, req, now) => {
        const inst = instanceOf(db, m[1]);
        const plan = planOf(db, inst.id);
        if (plan) settle(db, plan, now);
        const event = eventOf(db, inst.beatId);
        return {
            dateConfigs: {
                instanceId: inst.id,
                files: event.configLinks.map((link) => {
                    const file = db.configFiles.find((f) => f.id === link.configFileId);
                    const copy = plan?.stagedCopies.find((c) => c.fileId === link.configFileId);
                    return {
                        fileId: link.configFileId,
                        name: file?.name || link.configFileId,
                        source: copy ? 'OCCURRENCE' : 'BASE',
                        copies: copy ? [{ baseBranchId: copy.baseBranchId, branchId: copy.branchId, state: copy.state, changes: copy.changes }] : [],
                    };
                }),
            },
        };
    }],
    ['get', /^\/live-events\/([^/]+)$/, (db, m) => ({ liveEvent: eventOf(db, m[1]) })],
    ['get', /^\/live-events\/([^/]+)\/runs$/, (db, m) => ({
        liveEventRuns: db.runs.filter((r) => r.liveEventId === m[1]).sort((a, b) => b.ranAt.localeCompare(a.ranAt)),
    })],
    ['get', /^\/live-event-task-lists$/, (db, m, { params }) => ({ liveEventTaskLists: taskListsOf(db, params?.beatId) })],
    ['get', /^\/game-knowledge-snippets$/, (db, m, { params }) => ({
        gameKnowledgeSnippets: db.snippets.filter((s) => !params?.systemId || forEvent(s, params.systemId)),
    })],
    ['post', /^\/game-knowledge-snippets$/, (db, m, { data }, now) => {
        requireText(data?.title, 'A snippet requires a title');
        const row = {
            id: nextId(db, 'gameKnowledgeSnippet'), system: data.system || null, systemId: data.systemId || null, kind: data.kind || 'GENERAL',
            title: data.title.trim(), body: data.body || '', source: data.source || 'MANUAL', suggestedAction: data.suggestedAction || 'NONE',
            rule: null, createdAt: iso(now), updatedAt: iso(now),
        };
        db.snippets.push(row);
        return { gameKnowledgeSnippet: row };
    }],
    ['get', /^\/game-config\/files$/, (db) => db.configFiles.map(({ content, ...file }) => file)],
    ['get', /^\/player-segment$/, (db) => ({ segments: db.segments })],
];

const readStore = (gameId) => {
    try {
        const raw = sessionStorage.getItem(`${STORE_PREFIX}.${gameId}`);
        return raw ? JSON.parse(raw) : memory.get(gameId) || null;
    } catch {
        return memory.get(gameId) || null;
    }
};

const writeStore = (gameId, db) => {
    memory.set(gameId, db);
    try {
        sessionStorage.setItem(`${STORE_PREFIX}.${gameId}`, JSON.stringify(db));
    } catch (e) {
        console.warn('Upcoming mock: could not persist state', e);
    }
};

const firstDates = (db, placementId, now, count) => {
    const placement = db.placements.find((p) => p.id === placementId);
    return occurrences(eventOf(db, placement.beatId), placement, now, now + 90 * DAY).slice(0, count).map((o) => o.startMs);
};

const processedPlan = (db, placementId, startMs, listId, now) => {
    const inst = openInstance(db, placementId, startMs, now - 2 * HOUR);
    const plan = createPlan(db, inst.id, listId, now - 2 * HOUR);
    applyResults(db, plan, now - 2 * HOUR);
    return { inst, plan };
};

const seedScenario = (db, now) => {
    const [halloween] = firstDates(db, 'timelineItem-halloween', now, 1);
    if (halloween) processedPlan(db, 'timelineItem-halloween', halloween, 'liveEventTaskList-halloween', now);

    const [xpNext, xpAfter] = firstDates(db, 'timelineItem-weekend-xp', now, 2);
    if (xpNext) processedPlan(db, 'timelineItem-weekend-xp', xpNext, 'liveEventTaskList-xp', now);
    if (xpAfter) {
        const { plan } = processedPlan(db, 'timelineItem-weekend-xp', xpAfter, 'liveEventTaskList-xp', now);
        plan.revision += 1;
        plan.processingState = 'FAILED';
        plan.processingRevision = plan.revision;
        plan.failureReason = 'Processing could not finish: Connection refused: localhost/127.0.0.1:8080';
    }

    const [storeNext, storeAfter, storeThird] = firstDates(db, 'timelineItem-store', now, 3);
    if (storeNext) {
        const { plan } = processedPlan(db, 'timelineItem-store', storeNext, 'liveEventTaskList-store', now);
        stagePlan(db, plan, now - HOUR);
    }
    if (storeAfter) {
        const { plan } = processedPlan(db, 'timelineItem-store', storeAfter, 'liveEventTaskList-store', now);
        stagePlan(db, plan, now - HOUR);
        plan.tasks.forEach((t) => { if (t.actor === 'MANUAL') t.done = true; });
        publishPlan(db, plan, now - HOUR);
    }
    if (storeThird) {
        const inst = openInstance(db, 'timelineItem-store', storeThird, now - DAY);
        inst.skippedAt = iso(now - DAY);
        inst.skipReason = 'Store freeze for the launch week';
    }
};

const storeFor = (gameId, now) => {
    const existing = readStore(gameId);
    if (existing) return existing;
    const db = seedWorld(gameId, now);
    seedScenario(db, now);
    return db;
};

export const mockRequest = async (gameId, { method, path, data, params }) => {
    await new Promise((resolve) => { setTimeout(resolve, LATENCY_MS); });
    const now = Date.now();
    const db = storeFor(gameId || 'none', now);
    const route = ROUTES.find(([verb, re]) => verb === method && re.test(path));
    if (!route) throw notFound(path);
    try {
        return JSON.parse(JSON.stringify(route[2](db, path.match(route[1]), { data, params }, now)));
    } finally {
        writeStore(gameId || 'none', db);
    }
};

export const MOCK_LATENCY_MS = LATENCY_MS;

const wireChange = (a, i) => {
    if (a.type === 'SET_VALUE') {
        return {
            kind: 'change', id: `c${i + 1}`, type: 'config.set_value', target: { file_id: a.fileId, key: a.key }, params: { op: a.op, value: a.value },
        };
    }
    if (a.type === 'AUDIENCE') return { kind: 'change', id: `c${i + 1}`, type: 'event.audience', params: { segment_ids: [a.segmentId] } };
    return { kind: 'change', id: `c${i + 1}`, type: 'event.window', params: { hours: a.hours } };
};

// Intel's wire shape; `mockResult` rides along so the mock's chat-result keeps what its own run worked out.
const toWire = (taskId, result) => ({
    task_id: taskId,
    status: result.status,
    summary: result.note || '',
    outputs: [
        ...result.actions.map(wireChange),
        ...(result.status === 'COMPLETED' && !result.actions.length && result.note ? [{ kind: 'answer', format: 'text', body: result.note }] : []),
    ],
    questions: result.questions.map((q, i) => ({ id: `q${i + 1}`, text: q.text, options: q.options || [] })),
    depends_on: result.dependsOn,
    mockResult: result,
});

const chatReply = (view) => {
    if (view.kind === 'COMPLETED') return `Done: ${view.rows.join('; ')}. Apply it below; it still has to be staged and published on the plan.`;
    if (view.kind === 'ANSWERED') return 'Here is what I found. Apply it below to keep it on the plan.';
    if (view.kind === 'NEEDS_INPUT') return `It still needs more: ${view.rows.join(' ')}`;
    return `${view.summary || "I couldn't act on that."} Try rewording the task.`;
};

// Stands in for intel's live_event_task chat in the playground: runs only this task with the replies and never writes.
export const mockTaskChat = (gameId, {
    instanceId, taskId, answers = [], instruction = null,
}) => withMockWorld(gameId, (db, now) => {
    const plan = planFor(db, instanceId, now);
    const task = taskOf(plan, taskId);
    if (effectiveProcessingState(plan, now) === 'PROCESSING') return { reply: 'This date is running its tasks. Wait for them to finish, then send again.', cards: [] };
    if (task.actor === 'MANUAL') return { reply: "This task is a person's job. Tick or skip it on the plan.", cards: [] };
    const replies = [...(answers || []), ...(instruction ? [{ question: ADJUST, answer: instruction }] : [])];
    const tasks = activeTasks(plan).map((t) => (t.id === taskId ? { ...t, answers: mergeAnswers(t.answers, replies) } : t));
    const results = processTasks(tasks, inventoryFor(db, eventOf(db, plan.liveEventId), plan), iso(now), new Set([taskId]));
    const result = { ...results[taskId], dependsOn: results[taskId].dependsOn || [] };
    if (result.status === 'FAILED') return { reply: `The task couldn't finish: ${result.note || 'try again in a moment'}.`, cards: [] };
    const wire = toWire(taskId, result);
    const segmentName = (id) => db.segments.find((sg) => (sg.versionInfo?.parentEntityId || sg.id) === id)?.segmentName || id;
    return {
        reply: chatReply(proposalView(wire, segmentName)),
        cards: [{
            ok: true,
            action: 'update',
            category: 'live_event_run_task',
            gameId,
            id: taskId,
            api_record: {
                instanceId, taskId, liveEventId: plan.liveEventId, revision: plan.revision, answers: replies, saveToEvent: false, result: wire,
            },
        }],
    };
});

export const withMockWorld = (gameId, fn) => {
    const now = Date.now();
    const db = storeFor(gameId || 'none', now);
    try {
        const out = fn(db, now);
        return out === undefined ? out : JSON.parse(JSON.stringify(out));
    } finally {
        writeStore(gameId || 'none', db);
    }
};

export {
    instanceAt, instanceDto, nextId, notFound, openInstance, planOf, refused, upcomingRows,
};
